import { Button, Dialog, Stack } from "@mui/material";
import { useIsMounted } from "@shared/frontend/hooks";
import {
  useCallback,
  useEffect,
  useState,
  type ChangeEvent,
  type FC,
} from "react";
import Cropper, { type Area } from "react-easy-crop";
import { useAlert } from "../../../contexts/AlertProvider";
import { cropImage } from "./crop-image.function";

interface UploadImageInputProps {
  onImageUpload?: (image: File) => void;
  /** When false (e.g. opportunity cover), upload from file must use the cropper - no “full frame” bypass. */
  allowFullImageUpload?: boolean;
  inputId?: string;
}

const UploadImageInput: FC<UploadImageInputProps> = (props) => {
  const { onImageUpload, allowFullImageUpload = true, inputId = "fileInput" } = props;
  const [crop, setCrop] = useState<{ x: number; y: number; }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedImage, setCroppedImage] = useState<File | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [uploadedImageSrc, setUploadedImageSrc] = useState<
    string | undefined
  >();
  const { setAlertText, setSeverity } = useAlert();

  const isMounted = useIsMounted();

  // Revoke the active object URL on replacement, reset, or unmount -
  // covers the case where the parent unmounts us after a successful upload.
  useEffect(() => {
    if (!uploadedImageSrc) return;
    return () => URL.revokeObjectURL(uploadedImageSrc);
  }, [uploadedImageSrc]);

  const reset = useCallback(() => {
    setUploadedImageSrc(undefined);
    setUploadedFile(null);
    setZoom(1);
    setCrop({ x: 0, y: 0 });
    setCroppedImage(null);
  }, []);

  const handleFileUploaded = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadedFile(file);
    setUploadedImageSrc(URL.createObjectURL(file));
  }, []);

  const handleCrop = useCallback(
    async (_: Area, croppedAreaPixels: Area) => {
      if (!uploadedImageSrc) return;
      try {
        const cropped = await cropImage(uploadedImageSrc, croppedAreaPixels);
        if (isMounted.current) setCroppedImage(cropped);
      } catch {
        // Crop can fail on CORS-tainted images or oversized inputs.
        // Surface a user-facing alert; keep the dialog open so they can retry.
        if (isMounted.current) {
          setAlertText("לא ניתן היה לחתוך את התמונה");
          setSeverity("error");
        }
      }
    },
    [uploadedImageSrc, isMounted, setAlertText, setSeverity],
  );

  const handleSave = useCallback(() => {
    if (!croppedImage) {
      setAlertText("לא נבחרה תמונה");
      setSeverity("warning");
      return;
    }
    onImageUpload?.(croppedImage);
  }, [croppedImage, onImageUpload, setAlertText, setSeverity]);

  const handleUseFullImage = useCallback(() => {
    if (!uploadedFile) {
      setAlertText("לא נבחרה תמונה");
      setSeverity("warning");
      return;
    }
    onImageUpload?.(uploadedFile);
    reset();
  }, [uploadedFile, onImageUpload, reset, setAlertText, setSeverity]);

  return (
    <>
      {!uploadedImageSrc ? (
        <input
          id={inputId}
          type="file"
          accept="image/*"
          onChange={handleFileUploaded}
          style={{ display: "none" }}
        />
      ) : (
        <Dialog PaperProps={{ sx: { maxWidth: "unset" } }} open onClose={reset}>
          <Stack
            position="relative"
            width="90vw"
            height="90vh"
            justifyContent="flex-end"
          >
            <Cropper
              style={{ containerStyle: { height: "92%" } }}
              image={uploadedImageSrc}
              crop={crop}
              zoom={zoom}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={handleCrop}
              zoomSpeed={0.25}
              aspect={2.2}
              cropShape="rect"
            />
            <Stack
              direction="row"
              flexWrap="wrap"
              alignItems="center"
              mb={2}
              justifyContent="center"
              gap={1.5}
              px={1}
            >
              <Button variant="outlined" onClick={reset}>
                מחיקה
              </Button>
              {allowFullImageUpload ? (
                <Button variant="outlined" onClick={handleUseFullImage}>
                  כל התמונה
                </Button>
              ) : null}
              <Button variant="contained" onClick={handleSave}>
                שמירה
              </Button>
            </Stack>
          </Stack>
        </Dialog>
      )}
    </>
  );
};

export default UploadImageInput;
